import { Controller } from "@hotwired/stimulus"

/**
 * File Upload Stimulus Controller
 *
 * Enhanced drag & drop with:
 * - Multi-file support
 * - File size display
 * - Progress bar (via Active Storage direct upload or simulated)
 * - Remove individual files
 * - File type validation
 *
 * Usage:
 *   <div data-controller="file-upload"
 *        data-file-upload-max-size-value="10485760"
 *        data-file-upload-accept-value="image/*,.pdf,.doc,.docx">
 *     <div data-file-upload-target="dropzone"
 *          data-action="dragover->file-upload#dragOver dragenter->file-upload#dragOver dragleave->file-upload#dragLeave drop->file-upload#drop"
 *          class="border-2 border-dashed border-mod-border rounded-lg p-6 text-center cursor-pointer hover:border-mod-charcoal-soft transition-colors">
 *       <p class="text-sm text-mod-text-muted">Drag & drop files here, or click to browse</p>
 *       <input type="file" data-file-upload-target="input" data-action="change->file-upload#fileSelected" class="hidden" multiple>
 *     </div>
 *     <div data-file-upload-target="list" class="file-upload-list"></div>
 *     <div data-file-upload-target="progress" class="file-upload-progress hidden">
 *       <div data-file-upload-target="progressBar" class="file-upload-progress-bar" style="width: 0%"></div>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["dropzone", "input", "filename", "list", "progress", "progressBar"]
  static values = {
    maxSize: { type: Number, default: 10485760 }, // 10MB
    accept: { type: String, default: "" }
  }

  connect() {
    this.files = []
    if (this.hasDropzoneTarget && this.hasInputTarget) {
      this._dropzoneClick = (e) => {
        if (e.target !== this.inputTarget) this.inputTarget.click()
      }
      this.dropzoneTarget.addEventListener("click", this._dropzoneClick)
    }
  }

  disconnect() {
    if (this._dropzoneClick && this.hasDropzoneTarget) {
      this.dropzoneTarget.removeEventListener("click", this._dropzoneClick)
    }
  }

  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add("border-mod-charcoal", "bg-mod-warm-100")
    this.dropzoneTarget.classList.remove("border-mod-border")
  }

  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-mod-charcoal", "bg-mod-warm-100")
    this.dropzoneTarget.classList.add("border-mod-border")
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-mod-charcoal", "bg-mod-warm-100")
    this.dropzoneTarget.classList.add("border-mod-border")

    const droppedFiles = Array.from(event.dataTransfer.files)
    this.addFiles(droppedFiles)
  }

  fileSelected() {
    const selectedFiles = Array.from(this.inputTarget.files)
    this.addFiles(selectedFiles)
  }

  addFiles(newFiles) {
    for (const file of newFiles) {
      // Size check
      if (file.size > this.maxSizeValue) {
        this.showError(`${file.name} exceeds the ${this.formatSize(this.maxSizeValue)} limit`)
        continue
      }

      // Type check
      if (this.acceptValue && !this.isAcceptedType(file)) {
        this.showError(`${file.name} is not an accepted file type`)
        continue
      }

      this.files.push(file)
    }

    this.renderFileList()

    // Update the file input for form submission (single file fallback)
    if (this.files.length === 1) {
      const dt = new DataTransfer()
      dt.items.add(this.files[0])
      this.inputTarget.files = dt.files
    } else if (this.files.length > 1 && this.inputTarget.multiple) {
      const dt = new DataTransfer()
      this.files.forEach(f => dt.items.add(f))
      this.inputTarget.files = dt.files
    }

    // Legacy single-file support
    if (this.hasFilenameTarget && this.files.length > 0) {
      this.filenameTarget.textContent = this.files.length === 1
        ? `✓ ${this.files[0].name}`
        : `✓ ${this.files.length} files selected`
      this.filenameTarget.classList.remove("hidden")
    }
  }

  removeFile(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.files.splice(index, 1)
    this.renderFileList()

    // Update file input
    const dt = new DataTransfer()
    this.files.forEach(f => dt.items.add(f))
    this.inputTarget.files = dt.files
  }

  renderFileList() {
    if (!this.hasListTarget) return

    if (this.files.length === 0) {
      this.listTarget.innerHTML = ""
      return
    }

    this.listTarget.innerHTML = this.files.map((file, index) => `
      <div class="file-upload-item">
        <span class="file-upload-item-name" title="${this.escapeHtml(file.name)}">${this.escapeHtml(file.name)}</span>
        <span class="file-upload-item-size">${this.formatSize(file.size)}</span>
        <button type="button" class="file-upload-item-remove" data-index="${index}" data-action="click->file-upload#removeFile" aria-label="Remove">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `).join("")
  }

  // Called during Active Storage direct upload progress
  updateProgress(percent) {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove("hidden")
    }
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }
    if (percent >= 100) {
      setTimeout(() => {
        if (this.hasProgressTarget) {
          this.progressTarget.classList.add("hidden")
        }
      }, 500)
    }
  }

  isAcceptedType(file) {
    const accepts = this.acceptValue.split(",").map(a => a.trim().toLowerCase())
    const fileType = file.type.toLowerCase()
    const fileExt = `.${file.name.split(".").pop().toLowerCase()}`

    return accepts.some(accept => {
      if (accept.endsWith("/*")) {
        return fileType.startsWith(accept.replace("/*", "/"))
      }
      if (accept.startsWith(".")) {
        return fileExt === accept
      }
      return fileType === accept
    })
  }

  formatSize(bytes) {
    if (bytes === 0) return "0 B"
    const units = ["B", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`
  }

  showError(message) {
    document.dispatchEvent(new CustomEvent("toast:show", {
      detail: { message, type: "error", duration: 4000 }
    }))
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}

